import React from "react";
import type { DebugProtocol } from "./useDebugProtocolStore";

interface ProtocolListProps {
  protocols: DebugProtocol[];
  onSelectProtocol: (protocol: DebugProtocol) => void;
  selectedProtocolId?: string;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  theme: any;
}

export const ProtocolList: React.FC<ProtocolListProps> = ({
  protocols,
  onSelectProtocol,
  selectedProtocolId,
  theme,
}) => {
  const [sortBy, setSortBy] = React.useState<"timestamp" | "duration" | "name">(
    "timestamp",
  );
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [filterError, setFilterError] = React.useState(false);
  const [filterFork, setFilterFork] = React.useState(false);

  const handleSort = (field: "timestamp" | "duration" | "name") => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const filteredProtocols = React.useMemo(() => {
    let filtered = [...protocols];

    if (filterError) {
      filtered = filtered.filter((p) => p.error);
    }

    if (filterFork) {
      filtered = filtered.filter((p) => p.isFork);
    }

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case "timestamp":
          comparison = a.timestamp - b.timestamp;
          break;
        case "duration":
          comparison = a.duration - b.duration;
          break;
        case "name":
          comparison = a.operationName.localeCompare(b.operationName);
          break;
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [protocols, sortBy, sortOrder, filterError, filterFork]);

  const SortIndicator = ({ active }: { active: boolean }) => {
    if (!active) return null;
    return (
      <span style={{ marginLeft: "0.25rem", fontSize: "0.7rem" }}>
        {sortOrder === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Filters and controls */}
      <div
        style={{
          padding: "0.75rem 1rem",
          background: theme.cardHeaderBg,
          borderBottom: `1px solid ${theme.borderColor}`,
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <span
          style={{ fontSize: "0.875rem", fontWeight: "600", color: theme.text }}
        >
          {filteredProtocols.length} protocol
          {filteredProtocols.length !== 1 ? "s" : ""}
        </span>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            cursor: "pointer",
            fontSize: "0.875rem",
            color: theme.text,
          }}
        >
          <input
            type="checkbox"
            checked={filterError}
            onChange={() => setFilterError((prev) => !prev)}
            style={{ cursor: "pointer" }}
          />
          Errors Only
        </label>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            cursor: "pointer",
            fontSize: "0.875rem",
            color: theme.text,
          }}
        >
          <input
            type="checkbox"
            checked={filterFork}
            onChange={() => setFilterFork((prev) => !prev)}
            style={{ cursor: "pointer" }}
          />
          Forks Only
        </label>
      </div>

      {/* Table header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr 120px 100px 100px",
          gap: "0.5rem",
          padding: "0.5rem 1rem",
          background: theme.cardHeaderBg,
          borderBottom: `1px solid ${theme.borderColor}`,
          fontSize: "0.75rem",
          fontWeight: "600",
          color: theme.subtextColor,
          textTransform: "uppercase",
        }}
      >
        <div />
        <button
          type="button"
          onClick={() => handleSort("name")}
          style={{
            background: "none",
            border: "none",
            color: theme.subtextColor,
            cursor: "pointer",
            textAlign: "left",
            fontWeight: "600",
            fontSize: "0.75rem",
            textTransform: "uppercase",
            padding: 0,
          }}
        >
          Operation
          <SortIndicator active={sortBy === "name"} />
        </button>
        <button
          type="button"
          onClick={() => handleSort("timestamp")}
          style={{
            background: "none",
            border: "none",
            color: theme.subtextColor,
            cursor: "pointer",
            textAlign: "left",
            fontWeight: "600",
            fontSize: "0.75rem",
            textTransform: "uppercase",
            padding: 0,
          }}
        >
          Time
          <SortIndicator active={sortBy === "timestamp"} />
        </button>
        <button
          type="button"
          onClick={() => handleSort("duration")}
          style={{
            background: "none",
            border: "none",
            color: theme.subtextColor,
            cursor: "pointer",
            textAlign: "left",
            fontWeight: "600",
            fontSize: "0.75rem",
            textTransform: "uppercase",
            padding: 0,
          }}
        >
          Duration
          <SortIndicator active={sortBy === "duration"} />
        </button>
        <div style={{ textAlign: "center" }}>Status</div>
      </div>

      {/* Protocol list */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {filteredProtocols.length === 0 ? (
          <div
            style={{
              padding: "2rem",
              textAlign: "center",
              color: theme.subtextColor,
            }}
          >
            No protocols to display
          </div>
        ) : (
          filteredProtocols.map((protocol) => {
            const isSelected = protocol.id === selectedProtocolId;
            return (
              <button
                key={protocol.id}
                type="button"
                onClick={() => onSelectProtocol(protocol)}
                style={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "auto 1fr 120px 100px 100px",
                  gap: "0.5rem",
                  padding: "0.75rem 1rem",
                  background: isSelected ? theme.buttonBg : "transparent",
                  borderBottom: `1px solid ${theme.borderColor}`,
                  cursor: "pointer",
                  textAlign: "left",
                  border: "none",
                  color: theme.text,
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = theme.cardHeaderBg;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "transparent";
                  }
                }}
              >
                {/* Status indicator dot */}
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    marginTop: "0.35rem",
                    background: protocol.error
                      ? theme.errorColor
                      : protocol.isFork
                        ? theme.purpleColor
                        : theme.blueColor,
                  }}
                />

                {/* Operation name */}
                <div
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={protocol.operationName}
                >
                  {protocol.operationName}
                  {protocol.error && (
                    <span
                      style={{
                        marginLeft: "0.5rem",
                        fontSize: "0.75rem",
                        color: theme.errorColor,
                      }}
                    >
                      ({protocol.error})
                    </span>
                  )}
                </div>

                {/* Timestamp */}
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: theme.subtextColor,
                  }}
                >
                  {new Date(protocol.timestamp).toLocaleTimeString()}
                </div>

                {/* Duration */}
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: protocol.error
                      ? theme.errorColor
                      : protocol.duration > 100
                        ? theme.warningColor
                        : theme.successColor,
                    fontWeight: "500",
                  }}
                >
                  {protocol.duration}ms
                </div>

                {/* Status badges */}
                <div
                  style={{
                    display: "flex",
                    gap: "0.25rem",
                    justifyContent: "center",
                    fontSize: "0.65rem",
                  }}
                >
                  {protocol.isFork && (
                    <span
                      style={{
                        background: theme.purpleColor,
                        color: "white",
                        padding: "0.125rem 0.375rem",
                        borderRadius: "3px",
                        fontWeight: "600",
                      }}
                    >
                      FORK
                    </span>
                  )}
                  {protocol.error && (
                    <span
                      style={{
                        background: theme.errorColor,
                        color: "white",
                        padding: "0.125rem 0.375rem",
                        borderRadius: "3px",
                        fontWeight: "600",
                      }}
                    >
                      ERROR
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
